<template>
    <div class="error-msg-wrap">
        <div class="form-wrapper slide-up">
            <div class="form-close">
                <button type="button" class="form-close-x" title="Close" @click="$emit('close')">&times;</button>
            </div>
            <!-- Who can see the meeting: same rules as the API's checkMeetingAccess (meetings.ts) -->
            <div class="meeting-form-visibility">
                <template v-if="isPersonal">
                    <b>Only you and the attendees you pick</b> can see this meeting, its board and its history. Adding someone as an attendee is what gives them access.
                </template>
                <template v-else>
                    <b>Everyone on the {{ team }} team</b> can see this meeting, its board and its history. Outside the team, only Team Hub admins can.
                    Attendees are who you expect to be there; picking them doesn't change who can see it.
                </template>
            </div>
            <h3 class="meeting-form-label">Meeting name</h3>
            <input type="text" v-model="formData.Name" placeholder="Meeting Name" />

            <div class="meeting-form-row">
                <div>
                    <h3 class="meeting-form-label">Date</h3>
                    <input type="date" v-model="formData.Date" />
                </div>
                <div>
                    <h3 class="meeting-form-label">Repeats</h3>
                    <select v-model="formData.RecurrenceType">
                        <option value="once">One time</option>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="biweekly">Biweekly (every 2 weeks)</option>
                        <option value="monthly">Monthly</option>
                    </select>
                </div>
            </div>
            <!-- When it happens, stored like a calendar event (meetingTime.ts): wall-clock time in a time zone. Its length
                 is the sections' total, saved with it. -->
            <div class="meeting-form-row">
                <div>
                    <h3 class="meeting-form-label">Start time</h3>
                    <select v-model="formData.StartTime">
                        <option value="" disabled>Choose a time</option>
                        <option v-for="t in startTimes" :key="t.value" :value="t.value">{{ t.label }}</option>
                    </select>
                </div>
                <div>
                    <h3 class="meeting-form-label">Time zone</h3>
                    <select v-model="formData.TimeZone">
                        <option v-for="z in timeZones" :key="z" :value="z">{{ z.replace(/_/g, ' ') }}</option>
                    </select>
                </div>
            </div>

            <h3 class="meeting-form-label" style="margin-top:0">Sections <span class="meeting-form-hint">(set a time limit for each)</span></h3>
            <!-- Dragged by the handle to reorder, as Board Templates' columns -->
            <draggable v-model="formData.Sections" handle=".meeting-section-handle">
            <div class="meeting-section-row" v-for="(section, i) in formData.Sections" :key="i">
                <div class="meeting-section-top">
                    <span class="meeting-section-handle" title="Drag to reorder">&#8942;&#8942;</span>
                    <div class="meeting-section-num">{{ i + 1 }}</div>
                    <input type="text" v-model="section.name" placeholder="Section name" />
                    <svg-icon icon="x" class="meeting-section-remove" @click="removeSection(i)" />
                </div>
                <div class="meeting-section-bottom">
                    <div class="meeting-section-time">
                        <div class="meeting-form-sublabel">Time Limit</div>
                        <div class="meeting-section-time-input">
                            <input type="number" min="0" v-model.number="section.minutes" />
                            <span>min</span>
                        </div>
                    </div>
                    <div class="meeting-section-desc">
                        <div class="meeting-form-sublabel">Description</div>
                        <input type="text" v-model="section.description" placeholder="Description" />
                    </div>
                </div>
                <!-- While this section is on in Run Meeting, above the board. A personal meeting has no team to show. -->
                <div class="meeting-section-shows">
                    <label v-if="!isPersonal"><input type="checkbox" v-model="section.showScorecard" />Show the {{ team }} scorecard</label>
                    <label v-if="!isPersonal"><input type="checkbox" v-model="section.showRocks" />Show the {{ team }} rocks</label>
                    <label><input type="checkbox" v-model="section.hideBoard" />Hide the board</label>
                </div>
            </div>
            </draggable>
            <div class="meeting-form-addlink" @click="addSection()">+ Add Section</div>
            <div v-if="sectionsTotal" class="meeting-form-total">Meeting length: <b>{{ durationLabel(sectionsTotal) }}</b></div>

            <!-- A team meeting stays open to everyone on the team; these are who's expected,
                 and they're preselected when the meeting is started -->
            <div>
                <h3 class="meeting-form-label">Attendees<span v-if="!isPersonal" class="meeting-form-hint"> (optional)</span></h3>
                <div style="margin-bottom: 10px; max-height: 200px; overflow: auto;">
                    <div v-if="!employees.length" style="margin-bottom:10px">No employees to select.</div>
                    <div v-for="(emp, i) in employees" :key="i" class="checkbox-input">
                        <input type="checkbox" v-model="emp.selected" />
                        <div>{{ emp.Name }}</div>
                    </div>
                </div>
            </div>

            <input :class="disableButton ? 'disable-button' : ''" type="submit" :value="isEditing ? 'Save Meeting' : 'Create Meeting'" @click="submit()" />
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import SvgIcon from '@/components/SvgIcon.vue';
import { VueDraggableNext } from 'vue-draggable-next';
import { browserTimeZone, timeZoneOptions, startTimeHM, startTimeOptions, durationLabel } from '@/meetingTime';
import { parseSections, newSection } from '@/meetingSections';

export default defineComponent({
    name: 'AddEditMeeting',
    props: [ 'group', 'meeting', 'template' ],
    components: {
        SvgIcon,
        draggable: VueDraggableNext
    },
    data() {
        return {
            formData: {
                ID: null as any,
                Name: '',
                Date: '',
                RecurrenceType: 'once',
                Sections: [] as any,
                StartTime: '',
                TimeZone: browserTimeZone()
            },
            employees: [] as any,
            disableButton: false,
            templateColumns: null as any, // set from a chosen template; null = "Custom" (server keeps its default columns)
            templateItems: [] as any // default board items pulled off the template's columns, flattened for the create call
        }
    },
    computed: {
        isEditing(): boolean {
            return !!this.$props.meeting;
        },
        isPersonal(): boolean {
            return this.$props.group == 'my';
        },
        // A team meeting's team: the meeting's own when editing, else the open group tab
        team(): string {
            return this.isEditing ? this.$props.meeting.Team : this.$props.group;
        },
        // The meeting's length (saved as DurationMinutes, for its end time): the sections decide it
        sectionsTotal(): number {
            return this.formData.Sections.reduce((sum: number, s: any) => sum + (parseInt(s.minutes) || 0), 0);
        },
        // Every 15 minutes, plus a saved meeting's own time if it's off that grid
        startTimes(): { value: string, label: string }[] {
            return startTimeOptions(this.isEditing ? startTimeHM(this.$props.meeting) : '');
        },
        timeZones(): string[] {
            return timeZoneOptions(this.formData.TimeZone, browserTimeZone());
        }
    },
    async created() {
        if(this.isEditing) {
            const meeting = this.$props.meeting;
            this.formData.ID = meeting.ID;
            this.formData.Name = meeting.Name;
            this.formData.Date = new Date(meeting.Date).toISOString().split('T')[0];
            this.formData.RecurrenceType = meeting.RecurrenceType;
            this.formData.Sections = parseSections(meeting.SectionsJSON);
            // Meetings from before times existed start blank and must get one now
            this.formData.StartTime = startTimeHM(meeting);
            if(meeting.TimeZone) {
                this.formData.TimeZone = meeting.TimeZone;
            }
        } else if(this.$props.template) {
            const template = this.$props.template;
            this.formData.Name = template.Name;
            this.formData.Sections = parseSections(template.SectionsJSON);
            const parsedColumns = template.ColumnsJSON ? JSON.parse(template.ColumnsJSON) : null;
            this.templateColumns = parsedColumns;
            this.templateItems = (parsedColumns || []).flatMap((col: any) =>
                (col.items || [])
                    .filter((item: any) => item && item.name && String(item.name).trim())
                    .map((item: any) => ({
                        Name: item.name,
                        Description: item.description || '',
                        Priority: item.priority || null,
                        Column: col.name
                    }))
            );
            if(!this.formData.Sections.length) {
                this.addSection();
            }
        } else {
            this.addSection();
        }

        const attendeeIDs: number[] = this.isEditing && this.$props.meeting.AttendeeEmployeeIDsJSON
            ? JSON.parse(this.$props.meeting.AttendeeEmployeeIDsJSON)
            : [];

        // A team meeting only offers who can open it (the team's members with an account, and Team Hub admins),
        // plus anyone already saved as an attendee so they can still be unchecked
        const allowedIDs: number[] | null = this.isPersonal ? null
            : await axios.get(`${this.$store.state.apiUrl}/api/meetings/attendee-options`, { params: { team: this.team } })
                .then(resp => resp.data.map((id: any) => Number(id)) as number[])
                .catch(e => {
                    console.log(e);
                    return [] as number[];
                });

        await axios.get(`${this.$store.state.apiUrl}/api/employees/limitedinfo`)
            .then(resp => {
                const selfID = this.$store.state.user.employeeID;
                const offered = allowedIDs === null ? resp.data
                    : resp.data.filter((emp: any) => allowedIDs.includes(Number(emp.ID)) || attendeeIDs.includes(Number(emp.ID)));
                this.employees = offered.map((emp: any) => {
                    // tblEmployee.ID comes back from pg as a string (bigint), while the
                    // meeting's AttendeeEmployeeIDsJSON/employeeID are real numbers - normalize
                    // here so .includes()/=== comparisons against it actually match.
                    const id = Number(emp.ID);
                    return {
                        ...emp,
                        ID: id,
                        // New personal meeting: preselect yourself (a team meeting starts with nobody).
                        // Editing: reflect what's actually saved.
                        selected: this.isEditing ? attendeeIDs.includes(id) : this.isPersonal && id == selfID
                    };
                });
            })
            .catch(e => {
                console.log(e);
            })
    },
    methods: {
        durationLabel,
        addSection() {
            this.formData.Sections.push(newSection());
        },
        removeSection(i: number) {
            this.formData.Sections.splice(i, 1);
        },
        async submit() {
            if(!this.formData.Name || !this.formData.Date) {
                alert('Include a name and date');
                return;
            }
            if(!this.formData.StartTime) {
                alert('Include a start time');
                return;
            }
            if(!this.formData.Sections.length) {
                alert('Include at least one section');
                return;
            }
            for(const section of this.formData.Sections) {
                if(!section.name || !String(section.name).trim()) {
                    alert('Every section needs a name');
                    return;
                }
                if(!section.minutes || section.minutes <= 0) {
                    alert('Every section needs a time limit');
                    return;
                }
            }
            if(this.sectionsTotal > 1440) {
                alert('The sections add up to more than 24 hours');
                return;
            }
            if(this.isPersonal && !this.employees.some((emp: any) => emp.selected)) {
                alert('Select at least one attendee');
                return;
            }

            this.disableButton = true;

            const data: any = {
                Name: this.formData.Name,
                Date: this.formData.Date,
                RecurrenceType: this.formData.RecurrenceType,
                StartTime: this.formData.StartTime,
                TimeZone: this.formData.TimeZone,
                DurationMinutes: this.sectionsTotal,
                Sections: this.formData.Sections
            }
            data.AttendeeEmployeeIDs = this.employees.filter((emp: any) => emp.selected).map((emp: any) => emp.ID);

            if(this.isEditing) {
                data.ID = this.formData.ID;

                await axios.post(`${this.$store.state.apiUrl}/api/meetings/details`, data)
                    .then(() => {
                        this.$emit('updated');
                    })
                    .catch(e => {
                        console.log(e);
                        alert(e?.response?.data || 'There was an error');
                    })
            } else {
                data.Team = this.isPersonal ? null : this.$props.group;
                if(this.templateColumns) {
                    data.Columns = this.templateColumns;
                }
                if(this.templateItems.length) {
                    data.Items = this.templateItems;
                }

                await axios.post(`${this.$store.state.apiUrl}/api/meetings`, data)
                    .then(resp => {
                        this.$emit('created', resp.data);
                    })
                    .catch(e => {
                        console.log(e);
                        alert(e?.response?.data || 'There was an error');
                    })
            }

            this.disableButton = false;
        }
    }
})
</script>

<style scoped>
.meeting-form-label { margin-top: 20px; margin-bottom: 8px; font-size: 14px; }
.meeting-form-label:first-of-type { margin-top: 0; }
.meeting-form-visibility { background: var(--light-gray); border-radius: 8px; padding: 10px 14px; margin: 0 0 20px; font-size: 13px; line-height: 1.5; }
.meeting-form-hint { font-weight: normal; color: #a6a29d; font-size: 12px; }
.meeting-form-sublabel { font-size: 10px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; color: #a6a29d; margin-bottom: 4px; }
.meeting-form-row { display: flex; gap: 15px; }
.meeting-form-row > div { flex: 1; }
.meeting-form-addlink { color: #646464; font-weight: bold; font-size: 13px; cursor: pointer; margin: 0 0 15px; }
.meeting-form-addlink:hover { color: var(--primary-color); }
.meeting-form-total { margin: 4px 0 20px; font-size: 13px; color: var(--font-dark-gray); }

.meeting-section-row { border: 1px solid #E9E7E4; border-radius: 10px; padding: 12px 14px; margin-bottom: 10px; }
.meeting-section-top { display: flex; align-items: center; gap: 10px; }
.meeting-section-top input { margin-bottom: 0; flex: 1; }
.meeting-section-handle { cursor: move; color: #a6a29d; letter-spacing: -3px; font-size: 13px; padding: 0 4px 0 0; user-select: none; flex-shrink: 0; }
.meeting-section-num { width: 22px; height: 22px; border-radius: 50%; background: #F1EFEC; color: #646464; font-weight: 800; font-size: 11px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.meeting-section-remove { width: 14px !important; height: 14px !important; cursor: pointer; color: #a6a29d; flex-shrink: 0; }
.meeting-section-bottom { display: flex; gap: 16px; margin-top: 10px; }
.meeting-section-time input { margin-bottom: 0; width: 70px; }
.meeting-section-time-input { display: flex; align-items: center; gap: 6px; }
.meeting-section-time-input span { font-size: 13px; color: #646464; }
.meeting-section-desc { flex: 1; }
.meeting-section-desc input { margin-bottom: 0; width: 100%; }
.meeting-section-shows { display: flex; flex-wrap: wrap; gap: 6px 18px; margin-top: 10px; }
.meeting-section-shows label { display: inline-flex; align-items: center; gap: 7px; font-size: 13px; color: #646464; cursor: pointer; }
.meeting-section-shows input[type=checkbox] { width: 15px; height: 15px; margin: 0; accent-color: var(--primary-color); cursor: pointer; }
</style>
